import React, { useEffect, useRef, useState } from "react";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
import classNames from "classnames";
import getConfig from "next/config";
import dynamic from "next/dynamic";
import FocusLock from "react-focus-lock";
import { useDispatch, useSelector, useStore } from "react-redux";

import { createAcceptAgeGate } from "redux/action-creators/ageGate";
import { getShowAgeGate } from "redux/selectors/ageGate";
import { getDomainCountryCode } from "redux/selectors/config";

import Checkbox from "components/botanic/Checkbox";
import LegalLogo from "components/Icons/legal-logo.svg";

const CanadianAgeGateForm = dynamic(() => import("./CanadianAgeGateForm"));
const UsAgeGateForm = dynamic(() => import("./UsAgeGateForm"));

export type AgeGateFormProps = {
  confirm: () => void;
  decline?: () => void;
  setRegionSelected?: (regionSelected: boolean) => void;
};

const {
  publicRuntimeConfig: { leaflyDomain, leaflyCaDomain },
} = getConfig();
const REDIRECTS_BY_COUNTRY_CODE = { CA: leaflyCaDomain, US: leaflyDomain };

const AgeGate: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const domainCountryCode = useSelector(getDomainCountryCode);
  const showModal = useSelector(getShowAgeGate);
  const [rememberUser, setRememberUser] = useState(true);
  const [redirectingDomain, setRedirectingDomain] = useState<string>();
  const [ageIsInvalid, setAgeIsInvalid] = useState(false);
  const [regionSelected, setRegionSelected] = useState(false);
  const dispatch = useDispatch();
  const store = useStore();
  const decline = () => setAgeIsInvalid(true);

  const confirm = () => {
    dispatch(createAcceptAgeGate(rememberUser, store.getState()));
  };

  const redirectMaybe = (selectedCountryCode: "CA" | "US") => {
    if (selectedCountryCode !== domainCountryCode) {
      setRedirectingDomain(REDIRECTS_BY_COUNTRY_CODE[selectedCountryCode]);
      window.location = REDIRECTS_BY_COUNTRY_CODE[
        selectedCountryCode
      ] as unknown as Location;
    }
  };

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- containerRef.current will always be set here
    const scrollLockTarget = containerRef.current!;
    if (showModal) {
      /**
       * Record the container ref value here, as the reference to the element is
       * otherwise not available when the cleanup function of this useEffect is run.
       */
      disableBodyScroll(scrollLockTarget);
    } else {
      enableBodyScroll(scrollLockTarget);
    }

    return () => {
      enableBodyScroll(scrollLockTarget);
    };
  }, [showModal]);

  return (
    <>
      <style jsx global>
        {`
          .age-gate-background {
            background-color: #ffecd3;
            z-index: 2147483647;
          }
          .age-gate-container {
            width: calc(100% - 16px);
            max-width: 500px;
          }
          .age-gate-container select {
            background-color: var(--color-leafly-white);
            border: 1px solid var(--color-light-grey);
          }
          .age-gate-container select.border-error {
            border: 2px solid #dc0045;
            margin: -1px;
          }
          @media screen and (min-width: 1024px) {
            .age-gate-container {
              width: 476px;
            }
          }
        `}
      </style>
      <FocusLock disabled={!showModal}>
        <div
          className={classNames(
            "age-gate-background fixed flex inset-0 items-start md:h-auto md:items-center overflow-auto",
            { hidden: !showModal }
          )}
          ref={containerRef}
        >
          <div className="w-full md:h-auto mx-auto max-w-full">
            <div className="bg-white md:h-auto mx-auto my-sm p-lg lg:p-xxl text-sm age-gate-container shadow-md rounded max-w-full">
              <div className="mb-sm text-green">
                <LegalLogo width="144" height="64" data-testid="leafly-logo" />
              </div>
              <div
                className="font-headers font-extrabold mb-xl text-md"
                data-testid="age-gate-title-text"
              >
                Learn. Shop. Get cannabis.
              </div>

              <div className="pb-sm">
                <span className="text-xs mr-xs">Where are you from?</span>
                <select
                  className="bg-light-grey rounded font-bold items-center text-sm p-md px-lg z-20"
                  aria-label="Select your country"
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    redirectMaybe(
                      (e.target as HTMLSelectElement).value as "CA" | "US"
                    )
                  }
                  defaultValue={domainCountryCode}
                >
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                </select>
              </div>

              {domainCountryCode === "CA" ? (
                <CanadianAgeGateForm
                  confirm={confirm}
                  setRegionSelected={setRegionSelected}
                />
              ) : (
                <UsAgeGateForm confirm={confirm} decline={decline} />
              )}

              {(regionSelected || domainCountryCode !== "CA") && (
                <div className="my-md mt-lg">
                  <Checkbox
                    id="age-gate-remember"
                    checked={rememberUser}
                    onChange={() => setRememberUser(!rememberUser)}
                  >
                    <div className="ml-sm">
                      Remember me. <br />I confirm that this is not a shared
                      device.
                    </div>
                  </Checkbox>
                </div>
              )}
              {redirectingDomain && (
                <p>Redirecting you to {redirectingDomain}</p>
              )}
              {ageIsInvalid && (
                <p className="mt-xl text-error">
                  Darn! You're not old enough to use Leafly.
                </p>
              )}
            </div>
            <div className="mx-auto md:h-auto px-xl py-xl text-sm age-gate-container">
              <p className="text-cs mt-auto">
                By accessing this site, you accept the{" "}
                <a
                  className="py-xl"
                  data-testid="tou-link"
                  href="/info/terms-of-use"
                >
                  Terms of Use
                </a>{" "}
                and{" "}
                <a
                  className="py-xl"
                  data-testid="pp-link"
                  href="/info/privacy-policy"
                >
                  Privacy Policy
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </FocusLock>
    </>
  );
};

export default AgeGate;
